/* DoorbellRingDialog — Scan2Call orchestrator for the resident.
   -------------------------------------------------------------
   Wires the Agora call lifecycle (useResidentCall) + wallet math
   (useCallWallet) + ringtone into one of two views:
     • RingingCallView   — compact centred dialog while stage is
                            "ringing" or "connecting".
     • ConnectedCallView — fullscreen WhatsApp-style layout once the
                            call is up.

   Call waiting (Jul 2026): while this ring is being handled, a second
   visitor's ring surfaces as a slim banner (CallWaitingBanner). The
   resident can Decline it, or "End & answer" — which hangs up the
   current call and auto-answers the waiting one. The parent
   DoorbellListener owns the waiting slot; this component just renders
   the banner and reports the chosen action.

   Full two-way video call (camera + mic published upfront, see
   `useResidentCall.js` header).
*/
import { useEffect, useRef } from "react";
import { PhoneCall, PhoneOff, BellRing } from "lucide-react";
import useDoorbellTone from "./useDoorbellTone";
import { useResidentCall } from "../../hooks/useResidentCall";
import { useCallWallet } from "../../hooks/useCallWallet";
import { useWaitingPreview } from "../../hooks/useWaitingPreview";
import ConnectedCallView from "./ConnectedCallView";
import RingingCallView from "./RingingCallView";

function CallWaitingBanner({ waitingRing, connected, onSwitch, onDecline }) {
  const { previewOn, attachPreviewContainer } = useWaitingPreview(waitingRing);
  return (
    <div
      className="fixed top-3 left-3 right-3 z-[60] flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-900/95 backdrop-blur-md border border-amber-400/40 shadow-2xl"
      data-testid="call-waiting-banner"
    >
      {/* Live thumbnail of the waiting visitor (video-only subscribe —
          their audio never bleeds into the active call). Falls back to
          the pulsing bell while their video hasn't arrived. */}
      <div className="relative w-10 h-14 rounded-md overflow-hidden bg-black/60 border border-white/15 shrink-0">
        <div
          ref={attachPreviewContainer}
          className="absolute inset-0 [&>video]:w-full [&>video]:h-full [&>video]:object-cover"
          data-testid="call-waiting-thumb"
        />
        {!previewOn && (
          <div className="absolute inset-0 flex items-center justify-center">
            <BellRing className="w-4 h-4 text-amber-300 animate-pulse" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-white font-body truncate">
          {waitingRing.visitor_name || "Another visitor"} is also ringing
        </p>
        {waitingRing.visitor_location && (
          <p className="text-[10px] text-gray-400 font-body truncate">{waitingRing.visitor_location}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onDecline}
        className="w-9 h-9 rounded-full bg-red-500/20 border border-red-400/50 text-red-300 flex items-center justify-center hover:bg-red-500/35 transition-colors shrink-0"
        title="Decline waiting call"
        aria-label="Decline waiting call"
        data-testid="call-waiting-decline-btn"
      >
        <PhoneOff className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onSwitch}
        className="h-9 px-3 rounded-full bg-emerald-500 text-black text-xs font-bold font-body flex items-center gap-1.5 hover:bg-emerald-400 transition-colors shrink-0"
        data-testid="call-waiting-answer-btn"
      >
        <PhoneCall className="w-3.5 h-3.5" />
        {connected ? "End & answer" : "Answer instead"}
      </button>
    </div>
  );
}

export default function DoorbellRingDialog({
  ring,
  onClose,
  autoAnswer = false,
  waitingRing = null,
  onSwitchToWaiting,
  onDeclineWaiting,
}) {
  const call = useResidentCall({ ring, onEnded: onClose });
  const wallet = useCallWallet({
    enabled: call.stage === "connected",
    elapsed: call.elapsed,
  });

  // Ringtone plays while the call is in the ringing stage.
  useDoorbellTone(call.stage === "ringing");

  // Call-waiting promotion: the dialog remounts (key={ring_id}) with
  // autoAnswer=true — connect straight away, the resident already
  // tapped "End & answer".
  const autoAnsweredRef = useRef(false);
  useEffect(() => {
    if (autoAnswer && !autoAnsweredRef.current) {
      autoAnsweredRef.current = true;
      call.handleAnswer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAnswer]);

  // End the current leg (hangup if live, decline if still ringing),
  // then let the parent promote the waiting ring.
  const handleSwitch = async () => {
    if (call.stage === "connected") await call.handleHangup();
    else await call.handleDecline();
    onSwitchToWaiting?.();
  };

  const banner = waitingRing ? (
    <CallWaitingBanner
      waitingRing={waitingRing}
      connected={call.stage === "connected"}
      onSwitch={handleSwitch}
      onDecline={onDeclineWaiting}
    />
  ) : null;

  if (call.stage === "connected") {
    return (
      <>
        {banner}
        <ConnectedCallView
          ring={ring}
          elapsed={call.elapsed}
          muted={call.muted}
          videoOff={call.videoOff}
          hasCamera={call.hasCamera}
          facingMode={call.facingMode}
          barking={call.barking}
          netQuality={call.netQuality}
          deterrentId={call.deterrentId}
          selectedDeterrent={call.selectedDeterrent}
          attachRemoteContainer={call.attachRemoteContainer}
          attachLocalContainer={call.attachLocalContainer}
          hasWallet={wallet.hasWallet}
          isLowFunds={wallet.isLowFunds}
          fundedMm={wallet.fundedMm}
          fundedSs={wallet.fundedSs}
          nextDebitSs={wallet.nextDebitSs}
          pricePerMinNgn={wallet.pricePerMinNgn}
          onToggleMute={call.toggleMute}
          onToggleVideo={call.toggleVideo}
          onFlipCamera={call.flipCamera}
          onBark={call.handleBark}
          onPickDeterrent={call.pickDeterrent}
          onHangup={call.handleHangup}
        />
      </>
    );
  }

  return (
    <>
      {banner}
      <RingingCallView
        ring={ring}
        stage={call.stage}
        onAnswer={call.handleAnswer}
        onDecline={call.handleDecline}
        onHangup={call.handleHangup}
      />
    </>
  );
}
